import {
  currentTz,
  latestHlcWithinDrift,
  newId,
  type CheckinItemRecord,
  type CheckinLogRecord,
  type OccurrenceKey,
  type DailyNoteRecord,
  type ExportFile,
  type HlcClock,
  type ImportantDateRecord,
  type ProjectRecord,
  type SettingData,
  type SettingKey,
  type SettingRecord,
  type SettingValue,
  type Span,
  type TemplateRecord,
  type TimeBlockRecord,
} from '@wiztime/core';
import { OpLogKind } from '@wiztime/core';
import { createDexieRepo } from './dexie/dexie-repo';
import { migrateData, type MigrationOutcome } from './migrate';
import { opLogChangesOf, type OpLogInput } from './oplog';
import type { WizDb } from './dexie/db';
import { UndoConflictError, UndoConflictReason, ValidationError } from './errors';
import {
  clearBlock,
  clearBlocks,
  clearDrafts,
  confirmAllDrafts,
  confirmDraft,
  createBlock,
  editBlocks,
  moveBlocksToDate,
  setCountRank,
  setGroupProject,
  updateBlock,
  updateBlockSpans,
  type BlockPatch,
  type ConfirmResult,
  type CreateBlockOptions,
  type EditBlocksPatch,
  type SpanChange,
} from './mutations/blocks';
import {
  deleteCheckinItem,
  setCheckinItemActive,
  setCheckinItemStarred,
  markCheckinDone,
  setCheckinDoneAt,
  setCheckinStates,
  toggleCheckin,
  upsertCheckinItem,
  type CheckinItemInput,
  type CheckinStateChange,
} from './mutations/checkin';
import { changeDayStart } from './mutations/day-start';
import { CommandLabel } from './mutations/labels';
import { saveNote } from './mutations/notes';
import {
  createProjectAndAssign,
  type AssignTarget,
  type CreateProjectAndAssignResult,
} from './mutations/project-assign';
import {
  deleteProject,
  reorderProjects,
  setProjectActive,
  upsertProject,
  type ProjectInput,
} from './mutations/projects';
import { resolveOverlap, type OverlapResolution } from './mutations/resolve-overlap';
import { WriteSession } from './mutations/session';
import { setSetting, setSettings } from './mutations/settings';
import {
  generateDraftsForDate,
  mergeRecords,
  purgeExpiredDrafts,
  rehomeAllToDayStart,
  resetSettingsToDefaults,
  seedDefaults,
  type DefaultProjectNames,
  type MergeOptions,
  type MergeResult,
} from './mutations/system';
import {
  assertImportantDateExists,
  deleteImportantDate,
  setImportantDateActive,
  upsertImportantDate,
  type ImportantDateInput,
} from './mutations/important-dates';
import { deleteTemplate, upsertTemplate, type TemplateInput } from './mutations/templates';
import type { Repo } from './repo';
import { applyStep, StepDirection } from './undo/apply-step';
import type { UndoEntry, UndoListener, UndoState } from './undo/types';
import { MAX_UNDO_STEPS, UndoManager } from './undo/undo-manager';

export interface StoreOptions {
  readonly clock: HlcClock;
  /** Must equal `clock.deviceId`. */
  readonly deviceId: string;
  /** Wall clock in epoch milliseconds. */
  readonly now: () => number;
  /** IANA time zone written to new blocks; defaults to the environment's. */
  readonly tz?: string;
  /** Undo history size; defaults to 100 steps. */
  readonly undoCapacity?: number;
  /** Build identifier written to the operation log; defaults to 'dev'. */
  readonly appVersion?: string;
}

/** An operation-log entry the app records itself (reminder decisions, device-local settings). */
export interface AppOpLogInput {
  readonly kind: OpLogKind;
  readonly name: string;
  readonly summary?: Readonly<Record<string, unknown>>;
}

export interface DexieStoreOptions extends StoreOptions {
  readonly db: WizDb;
}

export interface RepoStoreOptions extends StoreOptions {
  readonly repo: Repo;
}

export interface UndoOutcome {
  /** The command that was undone or redone. */
  readonly label: CommandLabel;
  /** The records the command wrote, with their images before and after it. */
  readonly entries: readonly UndoEntry[];
}

export interface Store {
  readonly repo: Repo;
  readonly deviceId: string;
  /** Identity of the physical database under this store; see `Repo.dbInstanceId`. */
  getDbInstanceId(): Promise<string>;
  /**
   * Bring the stored data to this build's data format (data dictionary §10). Call once at start
   * and wait for it before any write; a `tooNew` or `failed` outcome means read-only.
   */
  migrate(): Promise<MigrationOutcome>;
  /** Record an operation the store does not see (data dictionary §11). */
  logOperation(input: AppOpLogInput): Promise<void>;

  // User commands: each is one transaction and one undo step.
  /** Creates a confirmed block, or returns null when the exact-duplicate guard suppressed it (ui-redesign §2.1.4). */
  createBlock(
    date: string,
    span: Span,
    projectId: string,
    options?: CreateBlockOptions,
  ): Promise<TimeBlockRecord | null>;
  updateBlock(id: string, patch: BlockPatch): Promise<TimeBlockRecord>;
  /** Change spans and delete blocks of one personal day as one transaction and one undo step. */
  updateBlockSpans(changes: readonly SpanChange[], deleteIds?: readonly string[]): Promise<TimeBlockRecord[]>;
  /** Move confirmed blocks to another personal date; a draft among them rejects the whole command. */
  moveBlocksToDate(ids: readonly string[], targetDate: string): Promise<TimeBlockRecord[]>;
  /** Count the overlapping minutes of this block towards it (ui-redesign §2.1.5). */
  setCountRank(id: string): Promise<TimeBlockRecord>;
  /**
   * Set the project of a merged group, confirming its drafts. With the project unchanged this
   * is the merged-group confirm path, which removes drafts that confirmed blocks of the same
   * project already cover (`removedAsDuplicate`, 2026-10b §10.1).
   */
  setGroupProject(ids: readonly string[], projectId: string): Promise<ConfirmResult>;
  /**
   * The picker's single commit (2026-10b §2.5): write the changed fields of a block or every
   * member of a merged group as one transaction and one undo step. The undo label is the
   * project-change label when the patch carries a project, else `EditBlocks`.
   */
  editBlocks(ids: readonly string[], patch: EditBlocksPatch): Promise<TimeBlockRecord[]>;
  /**
   * Create a project and assign it in one transaction and one undo step (2026-10b §2.5): to
   * existing blocks (with the picker's field changes) or as the project of a new block. A
   * failing block write leaves no project behind.
   */
  createProjectAndAssign(
    input: Omit<ProjectInput, 'id'>,
    target: AssignTarget,
  ): Promise<CreateProjectAndAssignResult>;
  clearBlock(id: string): Promise<void>;
  /** Clear several blocks (e.g. a merged group) as one transaction and one undo step; returns the cleared ids. */
  clearBlocks(ids: readonly string[]): Promise<string[]>;
  /** Confirm a draft; a draft that confirmed blocks of its project cover entirely is removed instead (§10.1). */
  confirmDraft(id: string, projectId?: string): Promise<ConfirmResult>;
  /** Confirm every visible draft of the date, removing the ones already covered by confirmed blocks (§10.1). */
  confirmAllDrafts(date: string): Promise<ConfirmResult>;
  clearDrafts(date: string): Promise<string[]>;
  /**
   * Flip one occurrence (data dictionary §3.2: its planned local date-time, or its date when
   * untimed). A tick records `doneAtMs` when given, otherwise the default completion instant.
   */
  toggleCheckin(itemId: string, occurrence: OccurrenceKey, doneAtMs?: number): Promise<CheckinLogRecord>;
  /** Tick the occurrence unless it is ticked already; null when nothing changed (no undo step). */
  markCheckinDone(itemId: string, occurrence: OccurrenceKey): Promise<CheckinLogRecord | null>;
  /** Set (or clear with null) the actual completion instant of a done occurrence; one undo step. */
  setCheckinDoneAt(itemId: string, occurrence: OccurrenceKey, doneAt: string | null): Promise<CheckinLogRecord>;
  /** Set the completion state of several occurrences in one undo step (the card's edit mode); returns logs written. */
  setCheckinStates(changes: readonly CheckinStateChange[]): Promise<number>;
  saveNote(date: string, text: string): Promise<DailyNoteRecord>;
  upsertProject(input: ProjectInput): Promise<ProjectRecord>;
  setProjectActive(id: string, active: boolean): Promise<ProjectRecord>;
  /** Set the display order of the projects to `ids` (missing ones follow in their old order); one undo step. */
  reorderProjects(ids: readonly string[]): Promise<ProjectRecord[]>;
  deleteProject(id: string): Promise<void>;
  upsertCheckinItem(input: CheckinItemInput): Promise<CheckinItemRecord>;
  setCheckinItemActive(id: string, active: boolean): Promise<CheckinItemRecord>;
  setCheckinItemStarred(id: string, starred: boolean): Promise<CheckinItemRecord>;
  deleteCheckinItem(id: string): Promise<void>;
  upsertTemplate(input: TemplateInput): Promise<TemplateRecord>;
  deleteTemplate(id: string): Promise<void>;
  /** Create or update an important date; the date must exist in its calendar (the lunar library loads on demand). */
  upsertImportantDate(input: ImportantDateInput): Promise<ImportantDateRecord>;
  setImportantDateActive(id: string, active: boolean): Promise<ImportantDateRecord>;
  deleteImportantDate(id: string): Promise<void>;
  setSetting<K extends SettingKey>(key: K, value: SettingValue<K>): Promise<SettingRecord>;
  /** Change several settings as one transaction and one undo step; an invalid entry changes none. */
  setSettings(entries: readonly SettingData[]): Promise<SettingRecord[]>;
  changeDayStart(newDayStartMin: number): Promise<void>;
  resolveOverlap(idA: string, idB: string, mode: OverlapResolution): Promise<void>;

  // Undo and redo. Both throw UndoConflictError when the step cannot be applied safely.
  undo(): Promise<UndoOutcome | null>;
  redo(): Promise<UndoOutcome | null>;
  getUndoState(): UndoState;
  subscribe(listener: UndoListener): () => void;
  clearUndoHistory(): void;

  // System operations: never undoable.
  generateDraftsForDate(date: string): Promise<TimeBlockRecord[]>;
  seedDefaults(names: DefaultProjectNames): Promise<ProjectRecord[]>;
  /**
   * Merge records pulled from another device (last writer wins). Invalid records, and records whose
   * hlc is more than 24 hours ahead of this device's clock, are reported in `rejected`, not applied.
   * Blocks outside the personal days of the current day start are re-homed (split when they cross a
   * day boundary); `changedIds` lists every record written, pieces included.
   * `options.remoteWinsTypes`: kinds whose incoming records replace the stored record even when the
   * stored one is newer (see `MergeOptions`).
   */
  applyRemote(records: readonly unknown[], options?: MergeOptions): Promise<MergeResult>;
  /**
   * First sync of a device that joins an existing space, step 1: reset every setting of this device to its
   * default (system-timestamp tombstones, never pushed; the space's records and later edits outrank them).
   * Nothing is re-homed. Returns the identifiers written.
   */
  resetSettingsToDefaults(): Promise<string[]>;
  /**
   * First sync of a device that joins an existing space, last step: re-home every block and daily-slot template
   * under the day start the stored settings resolve to (merged with `deferRehome`, see `MergeOptions`).
   * Returns the identifiers written.
   */
  rehomeAllToDayStart(): Promise<string[]>;
  /** Merge an export package (last writer wins); idempotent; clears the undo history. */
  importExport(file: ExportFile): Promise<MergeResult>;
  /**
   * Delete every record of this device, tombstones included, and clear the undo history. It
   * writes no tombstones, so it never spreads to other devices: with sync on, the caller must
   * turn sync off first (otherwise this device would hold a partial copy of the shared data).
   */
  clearAllData(): Promise<void>;
  purgeExpiredDrafts(dates: readonly string[]): Promise<string[]>;
}

/** The store on a Dexie database. */
export function createStore(options: DexieStoreOptions): Store {
  const { db, ...rest } = options;
  return createStoreFromRepo({ ...rest, repo: createDexieRepo(db) });
}

/** The store on any repository implementation. */
export function createStoreFromRepo(options: RepoStoreOptions): Store {
  const { repo, clock, deviceId, now } = options;
  if (clock.deviceId !== deviceId) {
    throw new ValidationError('The clock belongs to a different device than the store');
  }
  const tz = options.tz ?? currentTz();
  const appVersion = options.appVersion ?? 'dev';
  const undoManager = new UndoManager(options.undoCapacity ?? MAX_UNDO_STEPS);

  // One operation at a time per store, so undo order and clock order match call order.
  let queue: Promise<unknown> = Promise.resolve();
  function serial<T>(task: () => Promise<T>): Promise<T> {
    const run = queue.then(task);
    queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  // The clock must start past every timestamp already stored (the wall clock may have moved back),
  // except the ones further ahead than the drift limit: a bad record must not stall the clock.
  // Writes also catch up with the stored record they replace (see `WriteSession.write`), which
  // covers records another tab or a merge stored after this point.
  let primed: Promise<void> | undefined;
  function prime(): Promise<void> {
    primed ??= repo.maxHlc(latestHlcWithinDrift(now())).then(
      (max) => {
        if (max !== null) clock.receive(max);
      },
      (error: unknown) => {
        primed = undefined;
        throw error;
      },
    );
    return primed;
  }

  function opLog(
    kind: OpLogKind,
    name: string,
    nowMs: number,
    summary: Readonly<Record<string, unknown>>,
    changes: OpLogInput['changes'] = [],
  ): OpLogInput {
    return { at: new Date(nowMs).toISOString(), deviceId, appVersion, kind, name, summary, changes };
  }

  /** A merge's log entry: counts and record types only, not the records. */
  function mergeSummary(result: MergeResult, offered: number): Record<string, unknown> {
    return { offered, changed: result.changedIds.length, rejected: result.rejected.length };
  }

  function command<T>(label: CommandLabel, body: (session: WriteSession) => Promise<T>): Promise<T> {
    return serial(async () => {
      await prime();
      const nowMs = now();
      const { result, entries } = await repo.transaction(async (tx) => {
        const session = new WriteSession(tx, { clock, deviceId, nowMs, tz, newId: () => newId(nowMs) });
        const value = await body(session);
        const written = session.entries();
        if (written.length > 0) {
          await tx.appendOpLog(opLog(OpLogKind.Command, label, nowMs, { records: written.length }, opLogChangesOf(written)));
        }
        return { result: value, entries: written };
      });
      if (entries.length > 0) undoManager.record(label, entries, nowMs);
      return result;
    });
  }

  function system<T>(body: (nowMs: number) => Promise<T>): Promise<T> {
    return serial(async () => {
      await prime();
      return body(now());
    });
  }

  function step(direction: StepDirection): Promise<UndoOutcome | null> {
    return serial(async () => {
      const target = direction === StepDirection.Undo ? undoManager.peekUndo() : undoManager.peekRedo();
      if (target === undefined) return null;
      await prime();
      try {
        const nowMs = now();
        const written = await repo.transaction(async (tx) => {
          const applied = await applyStep(tx, { clock, deviceId, nowMs }, undoManager.guard, target, direction);
          const undo = direction === StepDirection.Undo;
          const changes = target.entries.map((entry) =>
            undo ? { id: entry.id, before: entry.after, after: entry.before ?? { ...entry.after, data: null } } : entry,
          );
          await tx.appendOpLog(
            opLog(undo ? OpLogKind.Undo : OpLogKind.Redo, target.label, nowMs, { records: changes.length }, opLogChangesOf(changes)),
          );
          return applied;
        });
        if (direction === StepDirection.Undo) undoManager.completeUndo(written);
        else undoManager.completeRedo(written);
        return { label: target.label, entries: target.entries };
      } catch (error) {
        if (error instanceof UndoConflictError) {
          if (error.reason === UndoConflictReason.RemoteChange) {
            undoManager.guard.resync(await repo.getMany(error.recordIds), error.recordIds);
          }
          if (direction === StepDirection.Undo) undoManager.dropUndoStep();
          else undoManager.dropRedoSteps();
        }
        throw error;
      }
    });
  }

  return {
    repo,
    deviceId,
    getDbInstanceId: () => repo.dbInstanceId(),
    migrate: () => serial(async () => {
      await prime();
      return migrateData({ repo, clock, nowMs: now, deviceId, appVersion });
    }),
    logOperation: (input) =>
      serial(() => repo.transaction((tx) => tx.appendOpLog(opLog(input.kind, input.name, now(), input.summary ?? {})))),

    createBlock: (date, span, projectId, opts) =>
      command(CommandLabel.CreateBlock, (s) => createBlock(s, date, span, projectId, opts)),
    updateBlock: (id, patch) => command(CommandLabel.UpdateBlock, (s) => updateBlock(s, id, patch)),
    updateBlockSpans: (changes, deleteIds) =>
      command(CommandLabel.UpdateBlockSpans, (s) => updateBlockSpans(s, changes, deleteIds)),
    moveBlocksToDate: (ids, targetDate) =>
      command(CommandLabel.MoveBlocksToDate, (s) => moveBlocksToDate(s, ids, targetDate)),
    setCountRank: (id) => command(CommandLabel.SetCountRank, (s) => setCountRank(s, id)),
    setGroupProject: (ids, projectId) =>
      command(CommandLabel.SetGroupProject, (s) => setGroupProject(s, ids, projectId)),
    editBlocks: (ids, patch) =>
      command(
        patch.projectId === undefined ? CommandLabel.EditBlocks : CommandLabel.SetGroupProject,
        (s) => editBlocks(s, ids, patch),
      ),
    createProjectAndAssign: (input, target) =>
      command(CommandLabel.CreateProjectAndAssign, (s) => createProjectAndAssign(s, input, target)),
    clearBlock: (id) => command(CommandLabel.ClearBlock, (s) => clearBlock(s, id)),
    clearBlocks: (ids) => command(CommandLabel.ClearBlocks, (s) => clearBlocks(s, ids)),
    confirmDraft: (id, projectId) =>
      command(CommandLabel.ConfirmDraft, (s) => confirmDraft(s, id, projectId)),
    confirmAllDrafts: (date) => command(CommandLabel.ConfirmAllDrafts, (s) => confirmAllDrafts(s, date)),
    clearDrafts: (date) => command(CommandLabel.ClearDrafts, (s) => clearDrafts(s, date)),
    toggleCheckin: (itemId, occurrence, doneAtMs) =>
      command(CommandLabel.ToggleCheckin, (s) => toggleCheckin(s, itemId, occurrence, doneAtMs)),
    markCheckinDone: (itemId, occurrence) =>
      command(CommandLabel.ToggleCheckin, (s) => markCheckinDone(s, itemId, occurrence)),
    setCheckinDoneAt: (itemId, occurrence, doneAt) =>
      command(CommandLabel.SetCheckinDoneAt, (s) => setCheckinDoneAt(s, itemId, occurrence, doneAt)),
    setCheckinStates: (changes) => command(CommandLabel.EditCheckinDay, (s) => setCheckinStates(s, changes)),
    saveNote: (date, text) => command(CommandLabel.SaveNote, (s) => saveNote(s, date, text)),
    upsertProject: (input) => command(CommandLabel.UpsertProject, (s) => upsertProject(s, input)),
    setProjectActive: (id, active) =>
      command(CommandLabel.SetProjectActive, (s) => setProjectActive(s, id, active)),
    reorderProjects: (ids) => command(CommandLabel.ReorderProjects, (s) => reorderProjects(s, ids)),
    deleteProject: (id) => command(CommandLabel.DeleteProject, (s) => deleteProject(s, id)),
    upsertCheckinItem: (input) => command(CommandLabel.UpsertCheckinItem, (s) => upsertCheckinItem(s, input)),
    setCheckinItemActive: (id, active) =>
      command(CommandLabel.SetCheckinItemActive, (s) => setCheckinItemActive(s, id, active)),
    setCheckinItemStarred: (id, starred) =>
      command(CommandLabel.SetCheckinItemStarred, (s) => setCheckinItemStarred(s, id, starred)),
    deleteCheckinItem: (id) => command(CommandLabel.DeleteCheckinItem, (s) => deleteCheckinItem(s, id)),
    upsertTemplate: (input) => command(CommandLabel.UpsertTemplate, (s) => upsertTemplate(s, input)),
    deleteTemplate: (id) => command(CommandLabel.DeleteTemplate, (s) => deleteTemplate(s, id)),
    upsertImportantDate: async (input) => {
      await assertImportantDateExists(input);
      return command(CommandLabel.UpsertImportantDate, (s) => upsertImportantDate(s, input));
    },
    setImportantDateActive: (id, active) =>
      command(CommandLabel.SetImportantDateActive, (s) => setImportantDateActive(s, id, active)),
    deleteImportantDate: (id) =>
      command(CommandLabel.DeleteImportantDate, (s) => deleteImportantDate(s, id)),
    setSetting: (key, value) => command(CommandLabel.SetSetting, (s) => setSetting(s, key, value)),
    setSettings: (entries) => command(CommandLabel.SetSettings, (s) => setSettings(s, entries)),
    changeDayStart: async (newDayStartMin) => {
      await command(CommandLabel.ChangeDayStart, (s) => changeDayStart(s, newDayStartMin));
    },
    resolveOverlap: (idA, idB, mode) =>
      command(CommandLabel.ResolveOverlap, (s) => resolveOverlap(s, idA, idB, mode)),

    undo: () => step(StepDirection.Undo),
    redo: () => step(StepDirection.Redo),
    getUndoState: () => undoManager.getState(),
    subscribe: (listener) => undoManager.subscribe(listener),
    clearUndoHistory: () => undoManager.clear(),

    generateDraftsForDate: (date) =>
      system((nowMs) =>
        repo.transaction(async (tx) => {
          const drafts = await generateDraftsForDate(tx, { clock, deviceId, nowMs, tz }, date);
          if (drafts.length > 0) {
            await tx.appendOpLog(opLog(OpLogKind.System, 'generateDrafts', nowMs, { date, created: drafts.length }));
          }
          return drafts;
        }),
      ),
    seedDefaults: (names) =>
      system((nowMs) =>
        repo.transaction(async (tx) => {
          const seeded = await seedDefaults(tx, { clock, deviceId, nowMs, tz }, names);
          if (seeded.length > 0) {
            await tx.appendOpLog(opLog(OpLogKind.System, 'seedDefaults', nowMs, { created: seeded.length }));
          }
          return seeded;
        }),
      ),
    applyRemote: (records, mergeOptions) =>
      system((nowMs) =>
        repo.transaction(async (tx) => {
          const result = await mergeRecords(tx, clock, records, nowMs, mergeOptions);
          if (result.changedIds.length > 0 || result.rejected.length > 0) {
            await tx.appendOpLog(opLog(OpLogKind.Sync, 'applyRemote', nowMs, mergeSummary(result, records.length)));
          }
          return result;
        }),
      ),
    resetSettingsToDefaults: () =>
      system((nowMs) =>
        repo.transaction(async (tx) => {
          const ids = await resetSettingsToDefaults(tx, deviceId);
          if (ids.length > 0) {
            await tx.appendOpLog(opLog(OpLogKind.System, 'resetSettingsToDefaults', nowMs, { reset: ids.length }));
          }
          return ids;
        }),
      ),
    rehomeAllToDayStart: () =>
      system((nowMs) =>
        repo.transaction(async (tx) => {
          const ids = await rehomeAllToDayStart(tx);
          if (ids.length > 0) {
            await tx.appendOpLog(opLog(OpLogKind.System, 'rehomeAllToDayStart', nowMs, { written: ids.length }));
          }
          return ids;
        }),
      ),
    importExport: (file) =>
      system(async (nowMs) => {
        const result = await repo.transaction(async (tx) => {
          const merged = await mergeRecords(tx, clock, file.records, nowMs);
          await tx.appendOpLog(
            opLog(OpLogKind.Import, 'importExport', nowMs, {
              ...mergeSummary(merged, file.records.length),
              exportedAt: file.exportedAt,
              dataFormat: file.dataFormat,
            }),
          );
          return merged;
        });
        undoManager.clear();
        return result;
      }),
    clearAllData: () =>
      system(async (nowMs) => {
        await repo.transaction(async (tx) => {
          const records = (await tx.listAll()).length;
          await tx.clearAll();
          await tx.appendOpLog(opLog(OpLogKind.Clear, 'clearAllData', nowMs, { records }));
        });
        // The migration backup holds the same data: replaced by the now empty database.
        await repo.backup();
        undoManager.clear();
      }),
    purgeExpiredDrafts: (dates) =>
      system((nowMs) =>
        repo.transaction(async (tx) => {
          const purged = await purgeExpiredDrafts(tx, { clock, deviceId, nowMs, tz }, dates);
          if (purged.length > 0) {
            await tx.appendOpLog(opLog(OpLogKind.System, 'purgeExpiredDrafts', nowMs, { purged: purged.length }));
          }
          return purged;
        }),
      ),
  };
}
